import { Building2, HandHeart, LockKeyhole, Zap } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "ui/select";

export type SwitchableTourCategory = 1 | 3 | 4 ;

interface TourTypeSwitcherProps {
  value: SwitchableTourCategory;
  onChange: (value: SwitchableTourCategory) => void;
  disabled?: boolean;
}

const tourTypes = {
  1: {
    label: "Instant",
    icon: Zap,
    iconClass: "bg-emerald-50 text-emerald-600",
    dotClass: "bg-emerald-500",
  },
  3: {
    label: "Faiz",
    icon: HandHeart,
    iconClass: "bg-amber-50 text-amber-600",
    dotClass: "bg-amber-500",
  },
  4: {
    label: "External",
    icon: Building2,
    iconClass: "bg-blue-50 text-blue-600",
    dotClass: "bg-blue-500",
  },
} as const;

export const TourTypeSwitcher = ({
  value,
  onChange,
  disabled = false,
}: TourTypeSwitcherProps) => {
  const selectedType = tourTypes[value];
  const SelectedIcon = selectedType.icon;

  return (
    <Select
      value={String(value)}
      onValueChange={(nextValue) =>
        onChange(Number(nextValue) as SwitchableTourCategory)
      }
      disabled={disabled}
    >
      <SelectTrigger
        className={`h-10 w-[145px] rounded-md px-2.5 text-sm shadow-sm transition-all sm:w-[160px] ${
          disabled
            ? "border-gray-200 bg-gray-50 text-gray-500 disabled:opacity-100"
            : "border-gray-300 bg-white hover:border-[#5B9BD5]"
        }`}
        aria-label="Tour Type"
        title={disabled ? "Tour type can only be changed while the tour is in Draft" : undefined}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${selectedType.iconClass}`}
          >
            <SelectedIcon className="h-3.5 w-3.5" />
          </span>
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-medium text-gray-800">
              {selectedType.label}
            </span>
            {disabled && <LockKeyhole className="h-3 w-3 text-gray-400" />}
          </span>
        </div>
      </SelectTrigger>
      <SelectContent
        align="end"
        className="w-[180px] rounded-lg border border-gray-200 p-1 shadow-lg"
      >
        {Object.entries(tourTypes).map(([category, option]) => {
          const OptionIcon = option.icon;

          return (
            <SelectItem
              key={category}
              value={category}
              className="rounded-md py-2 pl-2 pr-8 data-[state=checked]:bg-sky-50"
            >
              <span className="flex items-center gap-2">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${option.iconClass}`}
                >
                  <OptionIcon className="h-3.5 w-3.5" />
                </span>
                <span className="text-left text-sm font-medium text-gray-800">
                  {option.label}
                </span>
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};
